import useAuth from "./useAuth";
import { useGetClientById } from "./queries/usermanagement";
import { useMemo } from "react";

/**
 * Custom hook that provides comprehensive user authentication information
 * @returns {Object} User authentication data including user details, client info, and tokens
 */
const useUserAuthInfo = () => {
  const { user } = useAuth();

  // Safely extract user data with fallbacks
  const userData = useMemo(() => {
    if (!user?.data?.[0]) return null;
    return user.data[0];
  }, [user]);

  // Extract user account information
  const userAccount = useMemo(() => {
    if (!userData?.useraccount) return null;
    return userData.useraccount;
  }, [userData]);

  // Extract access token information
  const accessToken = useMemo(() => {
    if (!userData?.accessToken) return null;
    return userData.accessToken;
  }, [userData]);

  // Memoized user properties with safe extraction
  const clientid = useMemo(() => userAccount?.clientid, [userAccount]);
  const token = useMemo(() => accessToken?.token, [accessToken]);
  const userType = useMemo(() => userAccount?.usertype, [userAccount]);
  const name = useMemo(() => userAccount?.name, [userAccount]);
  const userid = useMemo(() => userAccount?.id, [userAccount]);
  const parentid = useMemo(() => accessToken?.id, [accessToken]);

  // Fetch client data only when clientid is available
  const getClient = useGetClientById(clientid);
  const clientData = getClient?.data?.data;

  // Check if user is authenticated
  const isAuthenticated = useMemo(() => {
    return !!(userData && token && userid);
  }, [userData, token, userid]);

  // Check if client data is loading
  const isLoadingClient = useMemo(() => {
    return getClient?.isLoading || false;
  }, [getClient?.isLoading]);

  // Check if client data has error
  const clientError = useMemo(() => {
    return getClient?.error || null;
  }, [getClient?.error]);

  return {
    // User data
    user,
    userData,
    userAccount,
    accessToken,

    // Authentication status
    isAuthenticated,

    // User properties
    clientid,
    token,
    userType,
    accountType: userType, // Alias for backward compatibility
    userid,
    parentid,
    name,

    // Client data
    clientName: clientData?.name,
    clientData,
    isLoadingClient,
    clientError
  };
};

export default useUserAuthInfo;
